
import {mergeArrays} from '@/utils/mergeArray';
import { IGenericObj, IPosItem } from './ctype';


// 检查两给range是否相交
export function checkRangeOverlap(focalRange: Range, testRange: Range) {
  const extremetiesOK = focalRange.compareBoundaryPoints(Range.END_TO_START, testRange) === -1;
  const overlapAreaOK = focalRange.compareBoundaryPoints(Range.START_TO_END, testRange) === 1;
  return extremetiesOK && overlapAreaOK;
}

export function checkRangeInside(rangeB: Range, rangeA: Range) {
  if (rangeB.compareBoundaryPoints(Range.START_TO_START, rangeA) <= 0 &&
    rangeB.compareBoundaryPoints(Range.END_TO_END, rangeA) >= 0) {
    // rangeA 完全在 rangeB 内部
    return true;
  }
  return false;
}

export function rangeIntersectsNode(range: Range, node: any) {
  const nodeRange = node.ownerDocument.createRange();
  try {
    nodeRange.selectNode(node);
  } catch (e) {
    nodeRange.selectNodeContents(node);
  }
  return checkRangeOverlap(range, nodeRange);
}

export function getNodesInRange(range: Range) {
  const startContainer = range.startContainer;
  const endContainer = range.endContainer;
  const commonAncestor = range.commonAncestorContainer;
  const textNodes = [];

  const isDisabledNode = (node: Node) => {
    let current: Node | null = node.parentNode;
    while (current) {
      if (current.nodeType === Node.ELEMENT_NODE && (current as Element).hasAttribute('data-disable')) {
        return true;
      }
      if (current === commonAncestor) {
        break;
      }
      current = current.parentNode;
    }
    return false;
  };

  // Use TreeWalker to traverse through all the nodes in the range
  const treeWalker = document.createTreeWalker(
    commonAncestor,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: function (node) {
        if (!isDisabledNode(node) && range.intersectsNode(node)) {
          return NodeFilter.FILTER_ACCEPT;
        }

        return NodeFilter.FILTER_REJECT;
      }
    }
  );

  let currentNode: any = treeWalker.currentNode;
  while (currentNode) {
    if (currentNode.nodeType === Node.TEXT_NODE && !isDisabledNode(currentNode) && range.intersectsNode(currentNode)) {
      let startOffset = 0;
      let endOffset = currentNode.nodeValue.length;

      // Adjust the startOffset for the startContainer
      if (currentNode === startContainer) {
        startOffset = range.startOffset;
      }

      // Adjust the endOffset for the endContainer
      if (currentNode === endContainer) {
        endOffset = range.endOffset;
      }

      textNodes.push({
        node: currentNode,
        startOffset: startOffset,
        endOffset: endOffset
      });
    }
    currentNode = treeWalker.nextNode();
  }

  return textNodes;
}

export function findClosestAncestorWithKey(node: any) {
  // 如果节点是文本节点，获取其父元素
  if (node.nodeType === Node.TEXT_NODE) {
    node = node.parentElement;
  }

  // 从元素节点开始查找最近的含有 data-key 属性的祖先元素
  while (node) {
    if (node.nodeType === Node.ELEMENT_NODE && node.hasAttribute('data-key')) {
      return node;
    }
    node = node.parentElement;
  }

  return null;
}


export function getValueFromKey(obj: any, key: string, replace?: any) {
  
  // if (key === '~question.content.order_ary.0') {
  let needNormal = false;
  let isArray = false;
  if (key[0] === '$') {
    needNormal = true;
    key = key.slice(1);
  } else if (key[0] === '~') {
    key = key.slice(1);
    isArray = true;
  }
  const keys = key.split('.');
  let value = obj;
  let lastObj = obj;
  const path = [];
  for (const k of keys) {
    if (value === null || typeof value !== 'object' || !Object.prototype.hasOwnProperty.call(value, k) || value[k] === undefined) {
      return undefined;
    }
    
    lastObj = value;
    value = value[k];
    path.push({obj: lastObj, key: k});
  }
  if (replace) {
    // Persisted marks can outlive a question's structure. Only text and
    // displayed numbers may be replaced; never overwrite a question object.
    if (typeof value !== 'string' && typeof value !== 'number') return value;
    const key = keys[keys.length - 1];
    
    if (needNormal) {
      lastObj[key + 'Html'] = replace(String(value));
    } else if (isArray) {
      const parent = path[path.length - 2];
      if (!parent) return value;
      const {obj, key} = parent;
      const {key: lastKey} = path[path.length - 1];
      if (!obj[key + 'Html'] || typeof obj[key + 'Html'] !== 'object') return value;
      obj[key + 'Html'][lastKey] = replace(String(value));
    } else {
      lastObj[key] = replace(String(value));
    }
  }

  return value;
}



type ActiveHighlight = {
  uid: string;
  hasPanel: boolean;
  renderOrder: number;
};

type SegmentDescriptor = {
  key: string;
  className: string;
  uids: string[];
  primaryUid?: string;
  noteUid?: string;
};

const escapeAttribute = (value: string) => value
  .replaceAll('&', '&amp;')
  .replaceAll('"', '&quot;')
  .replace(/'/g, '&#39;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;');

const getActiveHighlights = (positions: IPosItem[], offset: number): ActiveHighlight[] => {
  const activeByUid = new Map<string, ActiveHighlight>();
  positions.forEach((item, index) => {
    if (item.startOffset <= offset && offset < item.endOffset) {
      const current = activeByUid.get(item.uid);
      const next = {
        uid: item.uid,
        hasPanel: Boolean(item.hasPanel || current?.hasPanel),
        renderOrder: item.renderOrder ?? current?.renderOrder ?? index,
      };
      activeByUid.set(item.uid, next);
    }
  });
  return Array.from(activeByUid.values()).sort((left, right) => left.renderOrder - right.renderOrder);
};

const getSegmentDescriptor = (
  userPositions: IPosItem[],
  aiPositions: IPosItem[],
  userOffset: number,
  rawOffset: number
): SegmentDescriptor => {
  const active = getActiveHighlights(userPositions, userOffset);
  const noteHighlights = active.filter((item) => item.hasPanel);
  const hasAiHighlight = aiPositions.some((item) => item.startOffset <= rawOffset && rawOffset < item.endOffset);
  const classes: string[] = [];

  if (active.length) {
    classes.push('highLight');
    if (noteHighlights.length) {
      classes.push('highLightNote');
    } else if (active.length >= 2) {
      classes.push('highLightSecond');
    } else {
      classes.push('highLightFirst');
    }
  }
  if (hasAiHighlight) {
    classes.push('aiText');
  }

  const primary = noteHighlights.length
    ? noteHighlights[noteHighlights.length - 1]
    : active[active.length - 1];
  const uids = active.map((item) => item.uid);
  const noteUid = noteHighlights.length ? noteHighlights[noteHighlights.length - 1].uid : undefined;

  return {
    key: `${classes.join(' ')}|${uids.join(',')}|${primary?.uid || ''}|${noteUid || ''}`,
    className: classes.join(' '),
    uids,
    primaryUid: primary?.uid,
    noteUid,
  };
};

const renderSegment = (text: string, descriptor: SegmentDescriptor) => {
  if (!text || !descriptor.className) {
    return text;
  }
  const attributes = [
    `class="${descriptor.className}"`,
    descriptor.primaryUid ? `data-uid="${escapeAttribute(descriptor.primaryUid)}"` : '',
    descriptor.uids.length ? `data-uids="${escapeAttribute(descriptor.uids.join(','))}"` : '',
    descriptor.noteUid ? `data-note-uid="${escapeAttribute(descriptor.noteUid)}"` : '',
  ].filter(Boolean).join(' ');
  return `<span ${attributes}>${text}</span>`;
};

const insertHighlightMarkup = (source: string, userPositions: IPosItem[], aiPositions: IPosItem[] = []) => {
  const str = String(source);
  const protectedToken = /(\$[0-9]+\$|\n|<[^>]*>)/g;
  let result = '';
  let lastRawIndex = 0;
  let rawOffset = 0;
  let userOffset = 0;
  let bufferedText = '';
  let bufferedDescriptor: SegmentDescriptor | null = null;

  const flush = () => {
    if (bufferedDescriptor) {
      result += renderSegment(bufferedText, bufferedDescriptor);
    } else {
      result += bufferedText;
    }
    bufferedText = '';
    bufferedDescriptor = null;
  };

  const appendPlainText = (endIndex: number) => {
    for (let index = lastRawIndex; index < endIndex; index++) {
      const descriptor = getSegmentDescriptor(userPositions, aiPositions, userOffset, rawOffset);
      if (bufferedDescriptor && bufferedDescriptor.key !== descriptor.key) {
        flush();
      }
      if (!bufferedDescriptor) {
        bufferedDescriptor = descriptor;
      }
      bufferedText += str[index];
      rawOffset++;
      userOffset++;
    }
  };

  let match: RegExpExecArray | null;
  while ((match = protectedToken.exec(str)) !== null) {
    appendPlainText(match.index);
    flush();

    const token = match[0];
    result += token;
    rawOffset += token.length;
    if (token.startsWith('<')) {
      // The legacy selection coordinates include the hidden source markup length.
      userOffset += token.length;
    }
    lastRawIndex = protectedToken.lastIndex;
  }

  appendPlainText(str.length);
  flush();
  return result;
};

export function insertCharsAtPositions(str: string, positions: IPosItem[]) {
  return insertHighlightMarkup(str, positions);
}

export function insertCharsAtPositions2(str: string, positions: IPosItem[], positions2: IPosItem[]) {
  return insertHighlightMarkup(str, positions, positions2);
}



export function mergeIntervalObj(obj1: {posObj: IGenericObj<IPosItem[]>; posArr: IPosItem[];}, obj2: {posObj: IGenericObj<IPosItem[]>; posArr: IPosItem[];}) {

  const {posObj: posObj1, posArr: posArr1} = obj1;
  const {posObj: posObj2, posArr: posArr2} = obj2;
  
  
  const sameKeyArr = [];
  Object.keys(posObj1).reduce((memo, item, index) => {
    if (posObj2[item]) {
      sameKeyArr.push(item);
    }
    return memo;
  }, {});
  if (!sameKeyArr.length) {
    return {mergeObj: null, hasMerge: false, mergeArr: null};
  }

  const ret = mergeArrays(posArr1, posArr2);

  if (ret === false) {
    return {mergeObj: null, hasMerge: false, mergeArr: null};
  }

  const mergeObj = ret.reduce((memo: IGenericObj<IPosItem[]> , item) => {
    const {dataKey} = item;
    if (memo[dataKey]) {
      memo[dataKey].push(item);
    } else {
      memo[dataKey] = [item];
    }
    return memo;
  }, {});
  return {mergeObj, hasMerge: true, mergeArr: ret};

}

export function mergeIntervals1(...args: IPosItem[][]) {
  // 合并两个区间数组
  const intervals = args.reduce((acc, val) => acc.concat(val), []);

  // 按 startOffset 排序

  // 初始化合并后的区间数组
  const mergedIntervals = [];

  for (const interval of intervals) {
    if (
      mergedIntervals.length === 0 ||
      mergedIntervals[mergedIntervals.length - 1].endOffset < interval.startOffset
      // mergedIntervals[mergedIntervals.length - 1].endOffset === interval.startOffset
    ) {
      // 如果 mergedIntervals 为空，或者当前区间不与前一个区间重叠或相邻，直接添加到结果中
      mergedIntervals.push(interval);
    } else {
      // 否则，合并区间

      mergedIntervals[mergedIntervals.length - 1].endOffset = Math.max(
        mergedIntervals[mergedIntervals.length - 1].endOffset,
        interval.endOffset
      );
    }
  }
  return mergedIntervals;
}

export function mergeIntervals2(intervals: IPosItem[]) {
  // 合并两个区间数组
  // const intervals = args.reduce((acc, val) => acc.concat(val), []);

  // 按 startOffset 排序

  // 初始化合并后的区间数组
  const mergedIntervals = [];

  for (const interval of intervals) {
    if (
      mergedIntervals.length === 0 ||
      mergedIntervals[mergedIntervals.length - 1].endOffset < interval.startOffset ||
      mergedIntervals[mergedIntervals.length - 1].dataKey !== interval.dataKey
      // mergedIntervals[mergedIntervals.length - 1].endOffset === interval.startOffset
    ) {
      // 如果 mergedIntervals 为空，或者当前区间不与前一个区间重叠或相邻，直接添加到结果中
      mergedIntervals.push(interval);
    } else {
      // 否则，合并区间

      mergedIntervals[mergedIntervals.length - 1].endOffset = Math.max(
        mergedIntervals[mergedIntervals.length - 1].endOffset,
        interval.endOffset
      );
    }
  }
  return mergedIntervals;
}
function isContained(arrA: IPosItem[], arrB: IPosItem[]) {
  // 遍历数组B的每一个区间
  for (let i = 0; i < arrB.length; i++) {
    const b = arrB[i];
    let isContainedInA = false;

    // 遍历数组A的每一个区间，检查是否包含当前的B区间
    for (let j = 0; j < arrA.length; j++) {
      const a = arrA[j];

      // 检查A区间是否包含B区间
      if (a.startOffset <= b.startOffset && a.endOffset >= b.endOffset) {
        isContainedInA = true;
        break;
      }
    }

    // 如果B区间在A中没有找到任何包含它的区间，则返回false
    if (!isContainedInA) {
      return false;
    }
  }

  // 如果所有B区间都被A区间包含，则返回true
  return true;
}
export function isContain(wrapper: IGenericObj<IPosItem[]>, target: IGenericObj<IPosItem[]>) {
  if (!wrapper) {
    return false;
  }
  return Object.keys(target).every(key =>{
    if (!wrapper[key]) {
      return false;
    } else {
      return isContained(wrapper[key], target[key]);
    }
  });
}

export function isSelectionInElement(range: Range, element: HTMLElement) {
  const elementRange = document.createRange();
  elementRange.selectNodeContents(element);

  return range.compareBoundaryPoints(Range.START_TO_START, elementRange) >= 0 &&
         range.compareBoundaryPoints(Range.END_TO_END, elementRange) <= 0;
}
